import { FC, useState } from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { observer } from 'mobx-react-lite';

import { ListPagination } from '@components';
import { UsageFeature, UsageRangePreset, UsageService } from '@libs/constants';
import { useTrans } from '@libs/i18n';
import {
  BuildingsIcon,
  CurrencyDollarIcon,
  LightningIcon,
  WarningIcon,
} from '@phosphor-icons/react';

import { useAdminUsageOverview, useAdminUsageTenants } from '../api/admin-usage-api';
import { AdminPanel, AdminPanelHead } from '../components/AdminPanel';
import { adminColors } from '../components/adminTheme';
import { StatCard } from '../components/StatCard';
import { UsageBreakdownPanel } from '../components/usage/UsageBreakdownPanel';
import {
  formatCompactCount,
  formatCount,
  formatDelta,
  formatUsd,
} from '../components/usage/usageFormat';
import { UsageNumber } from '../components/usage/UsageNumber';
import { UsageRangePicker } from '../components/usage/UsageRangePicker';
import { UsageTenantsTable } from '../components/usage/UsageTenantsTable';
import { UsageTrendChart } from '../components/usage/UsageTrendChart';

const PAGE_SIZE = 20;

/**
 * Platform-wide usage & cost.
 *
 * Its own route rather than a panel on the admin dashboard: that page answers
 * "is the pipeline healthy right now" and is polled; this answers "what did we
 * pay over a window" and is not. Different question, different cadence.
 *
 * Reporting only: cost over a window, by service, feature and tenant. There
 * is no limit configuration here — see `UsageModule`.
 */
export const AdminUsagePage: FC = observer(() => {
  const { t } = useTrans();
  const theme = useTheme();
  const c = adminColors(theme);

  const [preset, setPreset] = useState<string>(UsageRangePreset.LAST_30D);
  const [page, setPage] = useState(1);

  const overview = useAdminUsageOverview({ preset });
  const tenants = useAdminUsageTenants({ preset, page, limit: PAGE_SIZE });

  const data = overview.data;
  const delta = data ? formatDelta(data.totals.costUsd, data.totals.previousCostUsd) : null;

  const featureLabel = (key: string) =>
    Object.values(UsageFeature).includes(key as never)
      ? t(`app.admin.usage.feature.${key}` as never)
      : key;
  const serviceLabel = (key: string) =>
    Object.values(UsageService).includes(key as never)
      ? t(`app.admin.usage.service.${key}` as never)
      : key;

  return (
    // `AdminLayout` is a fixed 100vh shell whose content pane is
    // `overflow: hidden`, so every page owns its own scroll — same as
    // `AdminTenantsPage` and `AdminTenantDetailPage`. Without `overflow: auto`
    // here the page is simply clipped at the viewport with no scrollbar.
    <Box
      sx={{
        flex: 1,
        overflow: 'auto',
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        p: { xs: 2, md: 3 },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        <Box>
          <Typography sx={{ fontSize: 20, fontWeight: 700, color: c.ink }}>
            {t('app.admin.usage.title')}
          </Typography>
          <Typography sx={{ fontSize: 12.5, color: c.faint, mt: 0.25 }}>
            {t('app.admin.usage.subtitle')}
            {data ? ` · ${t('app.admin.usage.range.tzLabel', { tz: data.range.tz })}` : ''}
          </Typography>
        </Box>
        <UsageRangePicker value={preset} onChange={setPreset} />
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' },
          gap: 1.75,
        }}
      >
        <StatCard
          icon={<CurrencyDollarIcon size={16} weight="bold" />}
          iconColor={c.green}
          label={t('app.admin.usage.stats.totalCost')}
          value={<UsageNumber>{data ? formatUsd(data.totals.costUsd) : '—'}</UsageNumber>}
          sub={delta ? t('app.admin.usage.stats.vsPrev', { delta }) : undefined}
        />
        <StatCard
          icon={<LightningIcon size={16} weight="bold" />}
          iconColor={c.azure}
          label={t('app.admin.usage.stats.calls')}
          value={<UsageNumber>{data ? formatCompactCount(data.totals.calls) : '—'}</UsageNumber>}
          sub={
            data
              ? t('app.admin.usage.stats.tokensSub', {
                  tokens: formatCompactCount(data.totals.totalTokens),
                })
              : undefined
          }
        />
        <StatCard
          icon={<BuildingsIcon size={16} weight="bold" />}
          iconColor={c.violet}
          label={t('app.admin.usage.stats.tenants')}
          value={
            <UsageNumber>{data ? formatCount(data.totals.tenantsWithUsage) : '—'}</UsageNumber>
          }
          sub={t('app.admin.usage.stats.tenantsSub')}
        />
        <StatCard
          icon={<WarningIcon size={16} weight="bold" />}
          iconColor={c.warn}
          label={t('app.admin.usage.stats.unpriced')}
          value={<UsageNumber>{data ? formatCount(data.unpriced.calls) : '—'}</UsageNumber>}
          sub={t('app.admin.usage.stats.unpricedSub')}
          accentBorder={data && data.unpriced.calls > 0 ? '#FFCCC7' : undefined}
        />
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '3fr 2fr' }, gap: 1.75 }}>
        <UsageTrendChart
          points={data?.trend ?? []}
          isLoading={overview.isLoading}
          title={t('app.admin.usage.trend.title')}
          sub={t('app.admin.usage.trend.subtitle')}
        />
        <UsageBreakdownPanel
          title={t('app.admin.usage.byService.title')}
          sub={t('app.admin.usage.byService.subtitle')}
          rows={data?.byService ?? []}
          isLoading={overview.isLoading}
          labelFor={serviceLabel}
          color={c.teal}
        />
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '2fr 3fr' }, gap: 1.75 }}>
        <UsageBreakdownPanel
          title={t('app.admin.usage.byFeature.title')}
          sub={t('app.admin.usage.byFeature.subtitle')}
          rows={data?.byFeature ?? []}
          isLoading={overview.isLoading}
          labelFor={featureLabel}
          color={c.violet}
        />

        <AdminPanel>
          <AdminPanelHead
            title={t('app.admin.usage.topTenants.title')}
            sub={t('app.admin.usage.topTenants.subtitle')}
          />
          <UsageTenantsTable
            rows={tenants.data?.rows ?? []}
            isLoading={tenants.isLoading}
            isError={tenants.isError}
            skeletonRows={Math.min(PAGE_SIZE, 8)}
            footer={
              tenants.data && tenants.data.meta.totalCount > PAGE_SIZE ? (
                <ListPagination
                  page={tenants.data.meta.currentPage}
                  totalPages={tenants.data.meta.totalPages}
                  onPageChange={setPage}
                  isLoading={tenants.isLoading}
                  summary={t('app.admin.usage.topTenants.summary', {
                    total: formatCount(tenants.data.meta.totalCount),
                  })}
                />
              ) : undefined
            }
          />
        </AdminPanel>
      </Box>
    </Box>
  );
});
